// ============================================
// Twin Fighter Service
// ============================================
//
// Thin wrapper around twinEngine + twinService.
// Runs automated "fights" between AI patient personas
// and the AI Nurse, scores them, and persists results.
// ============================================

import type {
  TestPersona,
  Message,
  CheckResult,
  CheckItem,
} from '../../types/patientJourney';
import {
  deriveBehaviorFromPersona,
  runTwinConversation,
  type ConversationCallbacks,
} from './twinEngine';
import { buildDefaultTouchpoint } from '../../data/personas/customerFunnelPersonas';
import { detectConversationModes, generateContextChecks } from './contextEngine';
import { claudeChat, claudeGenerate } from '../ai/providers/claude';
import { geminiChat } from '../ai/providers/gemini';
import { saveTestRun } from '../firestore/pjeService';
import { getActivePromptConfig } from '../firestore/promptService';

// --- Constants ---

const TWIN_MODEL = 'claude-sonnet-4-5-20250929';
const NURSE_MODEL = 'gemini-2.5-flash';
const SCORER_MODEL = 'claude-sonnet-4-5-20250929';
const MAX_TURNS = 12;
const NURSE_WIN_THRESHOLD = 70; // >= 70% pass rate = nurse wins

// --- Public Types ---

export interface FightCallbacks {
  onMessage: (msg: Message) => void;
  onCheckScored: (result: CheckResult, index: number, total: number) => void;
  onFightComplete?: (result: FightResult, index: number, total: number) => void;
}

export interface FightResult {
  fightId: string;
  personaId: string;
  personaName: string;
  personaArchetype: string;
  conversationLog: Message[];
  checkResults: CheckResult[];
  passRate: number;
  outcome: 'nurse_won' | 'persona_won';
  duration: number;
  startedAt: string;
  completedAt: string;
}

// --- Helpers ---

function generateFightId(): string {
  const hex = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, '0');
  return `fight_${hex()}${hex()}_${Date.now()}`;
}

/**
 * Merge context-based validation checks with the persona's own checklist.
 * Mirrors the pattern from TwinTest.tsx.
 */
function mergeContextChecks(persona: TestPersona): CheckItem[] {
  const modes = detectConversationModes(persona.contextModifiers);
  const contextChecks = generateContextChecks(modes);
  const existingIds = new Set(persona.validationChecklist.map((c) => c.checkId));
  const newChecks = contextChecks.filter((c) => !existingIds.has(c.checkId));
  return [...persona.validationChecklist, ...newChecks];
}

/**
 * Build ConversationCallbacks that wire into the client-side AI providers
 * (Claude for twin + scoring, Gemini for nurse).
 */
function buildFightCallbacks(callbacks: FightCallbacks): ConversationCallbacks {
  return {
    onMessage: callbacks.onMessage,
    onCheckScored: callbacks.onCheckScored,

    callTwin: async (systemPrompt, history, nurseMessage) => {
      return claudeChat(
        TWIN_MODEL,
        systemPrompt,
        history.map((h) => ({ role: h.role, content: h.content })),
        nurseMessage,
        0.8,
        1024,
      );
    },

    callNurse: async (systemPrompt, history, patientMessage) => {
      return geminiChat(
        NURSE_MODEL,
        systemPrompt,
        history.map((h) => ({
          role: h.role,
          parts: h.parts.map((p) => ({ text: p.text })),
        })),
        patientMessage,
        0.7,
        2048,
      );
    },

    scoreCheck: async (conversationText, check) => {
      const scoringPrompt = `You are evaluating an AI nurse conversation for quality assurance.

Given the conversation below, determine whether this validation check PASSED or FAILED.

VALIDATION CHECK:
- ID: ${check.checkId}
- Check: ${check.check}
- Category: ${check.category}
- Severity: ${check.severity}

CONVERSATION:
${conversationText}

Respond with EXACTLY this format (no other text):
RESULT: YES or NO
REASONING: Brief 1-2 sentence explanation`;

      const response = await claudeGenerate(
        SCORER_MODEL,
        'You are a clinical conversation quality evaluator. Be objective and strict. Answer only in the required format.',
        scoringPrompt,
        0,
        256,
      );

      const passMatch = response.match(/RESULT:\s*(YES|NO)/i);
      const reasonMatch = response.match(/REASONING:\s*(.+)/is);

      return {
        pass: passMatch ? passMatch[1].toUpperCase() === 'YES' : false,
        reasoning: reasonMatch ? reasonMatch[1].trim() : response.trim(),
      };
    },
  };
}

// --- Public API ---

/**
 * Run a single fight between one AI patient persona and the AI nurse.
 *
 * 1. Derives behavior from persona
 * 2. Builds touchpoint with merged context checks
 * 3. Runs conversation via twinEngine
 * 4. Scores conversation
 * 5. Persists result to Firestore
 * 6. Returns FightResult
 */
export async function runSingleFight(
  persona: TestPersona,
  callbacks: FightCallbacks,
): Promise<FightResult> {
  const fightId = generateFightId();
  const startedAt = new Date().toISOString();
  const startTime = Date.now();

  // 1. Derive behavior from the persona
  const behavior = deriveBehaviorFromPersona(persona);

  // 2. Build touchpoint with merged validation checks
  const baseTouchpoint = buildDefaultTouchpoint(persona.personaId);
  const mergedChecks = mergeContextChecks(persona);
  const touchpoint = { ...baseTouchpoint, validationChecklist: mergedChecks };

  // 3. Get active prompt template (if any)
  let promptTemplate: string | undefined;
  try {
    const config = await getActivePromptConfig();
    if (config?.template) {
      promptTemplate = config.template;
    }
  } catch {
    // Fall back to default prompt rules if Firestore fetch fails
  }

  // 4. Run the conversation + scoring via twinEngine
  const engineCallbacks = buildFightCallbacks(callbacks);
  const result = await runTwinConversation(
    persona,
    behavior,
    touchpoint,
    MAX_TURNS,
    engineCallbacks,
    promptTemplate,
  );

  // 5. Calculate outcome
  const completedAt = new Date().toISOString();
  const duration = Date.now() - startTime;
  const outcome: 'nurse_won' | 'persona_won' =
    result.passRate >= NURSE_WIN_THRESHOLD ? 'nurse_won' : 'persona_won';

  // 6. Save to Firestore
  try {
    await saveTestRun({
      runId: fightId,
      suiteId: 'twin-fighter',
      personaId: persona.personaId,
      touchpointId: touchpoint.touchpointId,
      startedAt,
      completedAt,
      status: 'completed',
      checkResults: result.checkResults,
      conversationLog: result.conversationLog,
      scorer: 'ai_twin',
      passRate: result.passRate,
      suiteVersion: 1,
    });
  } catch {
    // Firestore save is best-effort; don't fail the fight
  }

  // 7. Build and return FightResult
  const fightResult: FightResult = {
    fightId,
    personaId: persona.personaId,
    personaName: persona.name,
    personaArchetype: persona.archetype,
    conversationLog: result.conversationLog,
    checkResults: result.checkResults,
    passRate: result.passRate,
    outcome,
    duration,
    startedAt,
    completedAt,
  };

  return fightResult;
}

/**
 * Run a tournament: all selected personas fight the nurse sequentially.
 * Waits 2 seconds between fights for API rate limit headroom.
 */
export async function runTournament(
  personas: TestPersona[],
  callbacks: FightCallbacks,
): Promise<FightResult[]> {
  const results: FightResult[] = [];
  const total = personas.length;

  for (let i = 0; i < total; i++) {
    const persona = personas[i];

    // Run the fight
    const fightResult = await runSingleFight(persona, callbacks);
    results.push(fightResult);

    // Notify tournament progress
    callbacks.onFightComplete?.(fightResult, i, total);

    // Wait 2 seconds between fights (API rate limits), except after the last
    if (i < total - 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }

  return results;
}
